import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import PortalUploadPage from './pages/PortalUploadPage';
import PaymentReceiptPage from './pages/PaymentReceiptPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ConfigProvider } from './contexts/ConfigContext';
import LoginPage     from './pages/LoginPage';
import QuotePage     from './pages/QuotePage';
import QuotesPage    from './pages/QuotesPage';
import AdminPage     from './pages/AdminPage';
import ActivityPage  from './pages/ActivityPage';
import VoiceQuotePage from './pages/VoiceQuotePage';
import BundleQuotePage    from './pages/BundleQuotePage';
import MultiSiteQuotePage from './pages/MultiSiteQuotePage';
import ChangelogPage   from './pages/ChangelogPage';
import Layout          from './components/Layout';
import MarketRatesPage  from './pages/MarketRatesPage';
import FlexITQuotePage  from './pages/FlexITQuotePage';
import SoSPreviewPage   from './pages/SoSPreviewPage';
import ProposalsPage    from './pages/ProposalsPage';
// v3.5.99.7.13: standalone ETF calculator page
import ETFCalculatorPage from './pages/ETFCalculatorPage';
// v3.5.99.7.18: ETF letters list view (Phase B of the ETF tracking arc)
import ETFLettersPage from './pages/ETFLettersPage';
// v3.5.99.7.5: feedback widget for internal Ferrum users (@ferrumit.com)
import { useFeedbucket } from './lib/feedbucket';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={styles.loader}>Loading...</div>;
  return user ? children : <Navigate to="/login" replace />;
}

function RequireAdmin({ children }) {
  const { isAdmin, loading } = useAuth();
  if (loading) return <div style={styles.loader}>Loading...</div>;
  return isAdmin ? children : <Navigate to="/" replace />;
}

function AppRoutes() {
  // v3.5.99.7.5: mount Feedbucket widget for internal Ferrum users only
  useFeedbucket();
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/portal/upload/:token" element={<PortalUploadPage />} />
      <Route path="/pay/:sessionId" element={<PaymentReceiptPage />} />
      <Route path="/" element={<RequireAuth><Layout /></RequireAuth>}>
        <Route index           element={<QuotePage />} />
        <Route path="quotes"      element={<QuotesPage />} />
        <Route path="quotes/new"   element={<QuotePage />} />
        <Route path="quotes/:id"   element={<QuotePage />} />
        <Route path="voice"        element={<VoiceQuotePage />} />
        <Route path="voice/new"    element={<VoiceQuotePage />} />
        <Route path="voice/:id"    element={<VoiceQuotePage />} />
        <Route path="bundle"          element={<BundleQuotePage />} />
        <Route path="bundle/new"      element={<BundleQuotePage />} />
        <Route path="bundle/:id"      element={<BundleQuotePage />} />
        <Route path="multisite"       element={<MultiSiteQuotePage />} />
        <Route path="market-rates"     element={<MarketRatesPage />} />
        <Route path="flexIT/new"         element={<FlexITQuotePage />} />
        <Route path="flexIT/:id"         element={<FlexITQuotePage />} />
        <Route path="multisite/new"   element={<MultiSiteQuotePage />} />
        <Route path="multisite/:id"   element={<MultiSiteQuotePage />} />
        <Route path="changelog"  element={<ChangelogPage />} />
        {/* v3.5.99.7.13: ETF Calculator — available to any authenticated user */}
        <Route path="etf-calculator" element={<ETFCalculatorPage />} />
        {/* v3.5.99.7.18: ETF Letters list view */}
        <Route path="etf-letters" element={<ETFLettersPage />} />
        <Route path="admin"    element={<RequireAdmin><AdminPage /></RequireAdmin>} />
        <Route path="admin/sos/preview" element={<RequireAdmin><SoSPreviewPage /></RequireAdmin>} />
        <Route path="proposals" element={<RequireAdmin><ProposalsPage /></RequireAdmin>} />
        <Route path="activity" element={<RequireAdmin><ActivityPage /></RequireAdmin>} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ConfigProvider>
          <AppRoutes />
        </ConfigProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

const styles = {
  loader: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100vh', fontFamily: 'system-ui', color: '#6b7280', fontSize: 14
  }
};
